import slugify from "@sindresorhus/slugify";
import type {TopicItemDto} from "../dibAppApi/dibAppApi";
import {TOPIC_TYPE_PATHS, isTopicTypePathKey} from "../paths";
import {fileExtensionMap, isValidFileType} from "../features/content/contentUtils";
import type {ResourceType} from "../features/content/types";

export type TopicItemLink = {
    href: string;
    /** Set only for file topics: suggested filename for the download attribute. */
    download?: string;
};

export function getUrlForItem(item: TopicItemDto): TopicItemLink {
    // resourceTypeId arrives as a JSON number (or is absent/null on an old
    // API); ResourceType is string-valued, so convert before the guard.
    // Null/undefined/unknown/non-file all fail the guard → topic path (C4).
    const resourceTypeId =
        item.resourceTypeId == null
            ? undefined
            : (String(item.resourceTypeId) as ResourceType);
    if (isValidFileType(resourceTypeId) && item.id) {
        const extension = fileExtensionMap[resourceTypeId] ?? "";
        // Absolute legacy file URL — ExperimentalLink rewrites it to the
        // same-origin /dibappapi proxy when KAI Content View is on; off, it
        // opens the legacy app instead of a nonexistent KAI-origin route.
        return {
            href: buildLegacyFileUrl(item.id),
            download: `${item.name ?? ""}${extension}`,
        };
    }
    const key = parseInt(item.type ?? "", 10);
    if (isTopicTypePathKey(key)) {
        const topicPart = `/${TOPIC_TYPE_PATHS[key]}/${slugify(item.name ?? "t")}/${encodeURIComponent(item.id?.toLowerCase() ?? "")}`;
        return {href: `${getDibBaseUrl()}${topicPart}`};
    }
    return {href: ""};
}

/**
 * Returns the base URL for the dib app, picking the dev host when running
 * on localhost or ai.dibdev.no.
 */
export function getDibBaseUrl():
    | "https://dib.dibdev.no"
    | "https://app.dib.no" {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "ai.dibdev.no") {
        return "https://dib.dibdev.no";
    }
    return "https://app.dib.no";
}

/**
 * Checks whether a URL (full or relative) points to app.dib.no or dib.dibdev.no.
 */
export function isDibContentUrl(href: string): boolean {
    return toInternalPath(href) !== null;
}

/**
 * Absolute legacy file-download URL on the dib app host (app.dib.no /
 * dib.dibdev.no). This is the inverse of rewriteFileUrlForExperimental:
 * with KAI Content View off the link opens the legacy app, and
 * ExperimentalLink rewrites it to the same-origin /dibappapi/... route
 * when the flag is on. Must be absolute — a relative path would resolve
 * against the KAI origin instead of the legacy app.
 */
export function buildLegacyFileUrl(guid: string): string {
    return `${getDibBaseUrl()}/dibapi/document/file?resourceId=${encodeURIComponent(guid)}`;
}

/**
 * Legacy file URLs emitted by ai-backend always work on app.dib.no
 * (the host they target). In experimental mode the in-app surface is
 * better served by the same-origin /dibappapi/... shape, which the
 * KAI BFF proxies to dib-app-api.
 *
 * Converts (absolute or no-leading-slash relative):
 *   https://app.dib.no/dibapi/document/file?resourceId=<guid>
 *   dibapi/document/file?resourceId=<guid>
 *
 * to:
 *   /dibappapi/api/documents/<guid>/file
 *
 * Returns the input unchanged if it doesn't match the legacy file
 * pattern. Callers must gate on `isExperimental`.
 */
export function rewriteFileUrlForExperimental(href: string): string {
    if (!href) return href;
    const base = getDibBaseUrl();
    let url: URL;
    try {
        url = new URL(href, base);
    } catch {
        return href;
    }
    if (url.hostname !== "app.dib.no" && url.hostname !== "dib.dibdev.no") {
        return href;
    }
    // Pathname can be "/dibapi/document/file" (absolute input) or
    // "//dibapi/document/file" (no-leading-slash relative input
    // absolutised by the URL parser preserves the double slash).
    const isLegacyFilePath =
        url.pathname === "/dibapi/document/file" ||
        url.pathname.endsWith("/dibapi/document/file");
    if (!isLegacyFilePath) return href;
    const resourceId = url.searchParams.get("resourceId");
    if (!resourceId) return href;
    return `/dibappapi/api/documents/${encodeURIComponent(resourceId)}/file`;
}

/**
 * Converts any app.dib.no/dib.dibdev.no URL to an internal route path.
 *
 * Accepts:
 *   - Full URLs:    https://app.dib.no/tema/some-slug/some-id
 *   - Relative:     tema/some-slug/some-id
 *
 * Returns e.g. `/tema/some-slug/some-id`, or `null` if the URL
 * does not point to app.dib.no or dib.dibdev.no.
 */
export function toInternalPath(href: string): string | null {
    if (!href) return null;

    const base = getDibBaseUrl();
    try {
        const url = new URL(href, base);
        if (url.hostname !== "app.dib.no" && url.hostname !== "dib.dibdev.no")
            return null;
        // BFF-routed paths are not SPA routes — let callers fall through to a
        // plain anchor so the browser hits the BFF proxy instead of <Link>
        // (which would trigger "No routes matched location").
        if (
            url.pathname.startsWith("/dibapi/") ||
            url.pathname.startsWith("/dibappapi/")
        ) {
            return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}
